import type { ReactNode } from 'react';
import { Component, lazy, startTransition, Suspense, useEffect, useMemo, useState } from 'react';

import 'react-day-picker/style.css';
import { ApolloProvider } from '@apollo/client/react';
import { BricksProvider } from '@opendoor/bricks-next';
import { AddContextualData, addDatadogRUMGlobalContext } from '@opendoor/observability/slim';
import faviconPath from '@opendoor/shared-fe/faviconPath';
import '@js-joda/timezone/dist/js-joda-timezone-1970-2030';
import { isDevtoolsActive } from 'devtools/devtoolsEnabled';
import { CakeAtomsProvider } from 'platform/atoms';
import { AuthProvider } from 'platform/components/AuthContext';
import { LoaderProvider, SuspenseLoaderTracker } from 'platform/components/loader/LoaderContext';
import {
  ROOT_LOADER_APP_CONTENTS_ID,
  ROOT_LOADER_ID,
  ROOT_LOADER_INITIAL_SURFACE_ATTR,
  ROOT_LOADER_SURFACE_ATTR,
  RootLoader,
} from 'platform/components/loader/RootLoader';
import { RouteContentCommitSignal } from 'platform/components/loader/RouteContentCommitSignal';

import 'index.css';
import { CustomerBoundary } from 'platform/CustomerBoundary';
import { ErrorHandler } from 'platform/ErrorHandler';
import { useApptoolbox } from 'platform/hooks/useApptoolbox';
import { PageStalenessTimeProvider } from 'platform/hooks/useReloadStalePage';
import { useRootLoaderDone } from 'platform/hooks/useRootLoaderDone';
import { useLoadObservability, useObservability } from 'platform/observability';
import { ClientOnly } from 'platform/router/utils/ClientOnly';
import { TabVisibilityTrackerScript } from 'platform/TabVisibilityTrackerScript';
import { StyleSheet } from 'react-native';
import type { MetaFunction } from 'react-router';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { apolloClient } from 'setupClient';
import { IS_DEV } from 'shared/helpers/env';
import { useTrackInitialLoad } from 'shared/hooks/useTrackPerformance';
import Tamagui from 'tamagui.config';

// Lazy component must be created at module scope; creating it inside the
// component body produces a fresh lazy wrapper (and a fresh suspending
// Promise) on every render, which traps route transitions in a suspense loop.
const DevTools =
  IS_DEV && typeof window !== 'undefined' && isDevtoolsActive(window.location.search)
    ? lazy(async () => {
        const mod = await import('react-router-devtools/client');
        return { default: mod.withViteDevTools((): null => null) };
      })
    : null;

type OptionalDevToolsBoundaryState = {
  hasError: boolean;
};

class OptionalDevToolsBoundary extends Component<
  { children: ReactNode },
  OptionalDevToolsBoundaryState
> {
  override state: OptionalDevToolsBoundaryState = { hasError: false };

  static getDerivedStateFromError(): OptionalDevToolsBoundaryState {
    return { hasError: true };
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

const APP_CONTEXTUAL_DATA = { framework: 'react-router' } as const;
const NO_SCRIPT_STYLE_HTML = {
  __html: `
        <style>
          #${ROOT_LOADER_ID} { display: none !important; }
          #cake-noscript { display: grid !important; }
          #${ROOT_LOADER_APP_CONTENTS_ID} { display: none !important; }
        </style>
        `,
} as const;
// The no-JavaScript fallback must render correctly on its own — it shows when JS is
// disabled and cannot assume the app's atoms/Tailwind CSS bundle has loaded. Styles are
// therefore inline (self-contained) on purpose; do not convert these to className tokens.
const NO_SCRIPT_CONTAINER_STYLE = {
  minHeight: '100vh',
  display: 'none',
  placeItems: 'center',
  padding: 32,
  boxSizing: 'border-box',
  fontFamily: 'Graphik, sans-serif',
  color: '#23201d',
  background: '#f9f8f8',
  textAlign: 'center',
} as const;
const NO_SCRIPT_MESSAGE_STYLE = {
  margin: 0,
  maxWidth: 420,
  fontSize: 16,
  lineHeight: 1.5,
} as const;

type ReactNativeWebStyleSheet = {
  getSheet: () => { textContent: string };
};

const isReactNativeWebStyleSheet = (styleSheet: unknown): styleSheet is ReactNativeWebStyleSheet =>
  (typeof styleSheet === 'object' || typeof styleSheet === 'function') &&
  styleSheet !== null &&
  'getSheet' in styleSheet &&
  typeof styleSheet.getSheet === 'function';

const getReactNativeStyleSheetTextContent = (): string =>
  isReactNativeWebStyleSheet(StyleSheet) ? StyleSheet.getSheet().textContent : '';

// Pre-hydration surface pick — see comment where these are injected in <head>. Case-insensitive
// to match React Router's matchPath default so /Dashboard/Sell resolves the same way on the
// paint and hydration sides. Single source of truth for both the inline script string below and
// Layout's own hydration-matching computation, so the two can't drift apart.
const SELL_DASHBOARD_PATH_REGEX = /^\/dashboard\/sell(?:\/|$)/i;
const ROOT_LOADER_INITIAL_SURFACE_SCRIPT = {
  __html:
    `(function(){if(${SELL_DASHBOARD_PATH_REGEX}.test(location.pathname)){` +
    `document.documentElement.setAttribute('${ROOT_LOADER_INITIAL_SURFACE_ATTR}','contextual');}})();`,
} as const;
const ROOT_LOADER_INITIAL_SURFACE_STYLE = {
  __html: `
    :root[${ROOT_LOADER_INITIAL_SURFACE_ATTR}="contextual"] [${ROOT_LOADER_SURFACE_ATTR}="simple"] {
      opacity: 0 !important;
      transform: translateY(-8px) scale(0.995) !important;
      pointer-events: none !important;
    }
    :root[${ROOT_LOADER_INITIAL_SURFACE_ATTR}="contextual"] [${ROOT_LOADER_SURFACE_ATTR}="contextual"] {
      opacity: 1 !important;
      transform: translateY(0) scale(1) !important;
      pointer-events: auto !important;
    }
  `,
} as const;
// Runs after the RootLoader markup is parsed so screen readers see the correct
// surface during the pre-hydration paint window (CSS can flip opacity but not
// aria-hidden). React re-syncs both attributes on the first commit.
const ROOT_LOADER_INITIAL_SURFACE_ARIA_SCRIPT = {
  __html:
    `(function(){if(document.documentElement.getAttribute('${ROOT_LOADER_INITIAL_SURFACE_ATTR}')!=='contextual'){return;}` +
    `var s=document.querySelector('[${ROOT_LOADER_SURFACE_ATTR}="simple"]');` +
    `var c=document.querySelector('[${ROOT_LOADER_SURFACE_ATTR}="contextual"]');` +
    `if(s){s.setAttribute('aria-hidden','true');}` +
    `if(c){c.setAttribute('aria-hidden','false');}})();`,
} as const;

// Injects devtools when we're in dev and clientside.
// Local Suspense with a null fallback keeps a devtools chunk miss from swapping
// the whole app to LoadingPage via the outer Suspense boundary.
function LazyDevTools(): ReactNode {
  if (!DevTools) {
    return null;
  }
  const DevToolsComponent = DevTools;

  return (
    <ClientOnly>
      {() => (
        <OptionalDevToolsBoundary>
          <Suspense fallback={null}>
            <DevToolsComponent />
          </Suspense>
        </OptionalDevToolsBoundary>
      )}
    </ClientOnly>
  );
}

function AppContents({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Wrapped in a startTransition to avoid blocking the main thread
    startTransition(() => {
      addDatadogRUMGlobalContext({ framework: 'react-router' });
    });
  }, []);
  useLoadObservability();
  useTrackInitialLoad();
  useApptoolbox();

  return (
    <AddContextualData data={APP_CONTEXTUAL_DATA}>
      <ApolloProvider client={apolloClient()}>
        {/* Renders its own <script> tag with no BricksProvider/context dependency, so it stays
          outside ClientOnly below: it exists specifically to bind its event listener from the
          initial document, before JS bundles load, and delaying it until after hydration would
          defeat that (and skew the time-tab-visible metric it feeds). */}
        <TabVisibilityTrackerScript />
        {/* BricksProvider (Tamagui) has to be inside ClientOnly, not just AuthProvider below
          it: Tamagui's root theme span computes its inline style from internal state that
          isn't stable between the prerendered build and the real client, a hydration
          mismatch on every load. Deferring the whole Tamagui tree to client-only avoids ever
          hydration-matching it. */}
        <ClientOnly fallback={<SuspenseLoaderTracker name="suspense:client-only" />}>
          {() => (
            <BricksProvider useObservability={useObservability}>
              <CakeAtomsProvider>
                <Suspense fallback={<SuspenseLoaderTracker name="suspense:root" />}>
                  <AuthProvider>{children}</AuthProvider>
                </Suspense>
                <LazyDevTools />
              </CakeAtomsProvider>
            </BricksProvider>
          )}
        </ClientOnly>
      </ApolloProvider>
    </AddContextualData>
  );
}

function AppContentsGate({ children }: { children: ReactNode }) {
  const rootLoaderDone = useRootLoaderDone();
  const [hasInitialRootLoaderFinished, setHasInitialRootLoaderFinished] = useState(rootLoaderDone);

  useEffect(() => {
    if (rootLoaderDone) {
      setHasInitialRootLoaderFinished(true);
    }
  }, [rootLoaderDone]);

  return (
    // eslint-disable-next-line @opendoor/no-html-elements -- wraps BricksProvider, so Box is not available yet
    <div
      id={ROOT_LOADER_APP_CONTENTS_ID}
      aria-hidden={hasInitialRootLoaderFinished ? undefined : true}
      {...(hasInitialRootLoaderFinished ? {} : { inert: '' })}
    >
      {children}
    </div>
  );
}

/* eslint-disable @opendoor/no-html-elements -- no-JS fallback renders outside BricksProvider */
function NoScriptFallback() {
  return (
    <>
      <noscript dangerouslySetInnerHTML={NO_SCRIPT_STYLE_HTML} />
      {/* eslint-disable-next-line react/forbid-dom-props -- no-JS fallback must be self-contained (see NO_SCRIPT_CONTAINER_STYLE) */}
      <div id="cake-noscript" style={NO_SCRIPT_CONTAINER_STYLE}>
        {/* eslint-disable-next-line react/forbid-dom-props -- no-JS fallback must be self-contained */}
        <p style={NO_SCRIPT_MESSAGE_STYLE}>
          JavaScript is required to load this page. Please enable JavaScript and refresh.
        </p>
      </div>
    </>
  );
}
/* eslint-enable @opendoor/no-html-elements */

export const meta: MetaFunction = () => [{ title: 'Opendoor' }];

// Rendered on both prerender and client, so React owns the full document and
// route-level <Meta />/<Links /> updates reach the DOM.
export function Layout({ children }: { children: ReactNode }) {
  const tamaguiCSS = Tamagui.getCSS();
  const reactNativeStylesheetHTML = useMemo(
    () => ({
      __html: getReactNativeStyleSheetTextContent(),
    }),
    [],
  );
  const tamaguiStylesheetHTML = useMemo(() => ({ __html: tamaguiCSS }), [tamaguiCSS]);
  // Computed once, at mount, to match what the pre-hydration script below already set
  // imperatively on this same element before React loaded — never recomputed afterward, so
  // once RootLoader clears the real DOM attribute post-mount, this stays frozen and React has
  // no reason to write it back. Absent during the static prerender build (no window there),
  // matching the prerendered shell, which never had this attribute either.
  const [initialLoaderSurface] = useState<'contextual' | undefined>(() =>
    typeof window !== 'undefined' && SELL_DASHBOARD_PATH_REGEX.test(window.location.pathname)
      ? 'contextual'
      : undefined,
  );

  return (
    <html
      lang="en"
      {...(initialLoaderSurface && { [ROOT_LOADER_INITIAL_SURFACE_ATTR]: initialLoaderSurface })}
    >
      <head>
        <Meta />
        <meta name="framework" content="react-router" />
        <meta charSet="utf-8" />
        <link rel="shortcut icon" href={faviconPath({ railsEnv: import.meta.env.ODFE_ENV })} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#FAF9F6" />
        <meta name="msapplication-navbutton-color" content="#FAF9F6" />
        <meta name="apple-mobile-web-app-status-bar-style" content="#FAF9F6" />
        <Links />
        {/* Preload fonts to aid with CLS and LCP. URLs must match the @font-face
          src in index.css exactly so the browser reuses the preload. */}
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Bold-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Semibold-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Medium-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Regular-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <style
          id="react-native-stylesheet"
          suppressHydrationWarning
          dangerouslySetInnerHTML={reactNativeStylesheetHTML}
        />
        <style suppressHydrationWarning dangerouslySetInnerHTML={tamaguiStylesheetHTML} />
        {/*
          Pre-hydration surface pick. The static shell is prerendered at "/", so it hard-codes
          the simple loader visible. On a sell hard load we swap surfaces via CSS !important
          before React hydrates; RootLoader clears the attribute after mount so React drives
          visibility from that point on.
        */}
        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={ROOT_LOADER_INITIAL_SURFACE_SCRIPT}
        />
        <style
          suppressHydrationWarning
          dangerouslySetInnerHTML={ROOT_LOADER_INITIAL_SURFACE_STYLE}
        />
      </head>
      <body id="root">
        <LoaderProvider>
          <RootLoader />
          {/* Aria sync must run after the loader markup is parsed (surface divs
              exist by this point). Kept minimal — just brings aria-hidden into
              agreement with the CSS-driven paint. */}
          <script
            suppressHydrationWarning
            dangerouslySetInnerHTML={ROOT_LOADER_INITIAL_SURFACE_ARIA_SCRIPT}
          />
          <NoScriptFallback />
          <AppContentsGate>
            <AppContents>{children}</AppContents>
          </AppContentsGate>
        </LoaderProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <CustomerBoundary>
      <PageStalenessTimeProvider>
        <Outlet />
        <RouteContentCommitSignal />
      </PageStalenessTimeProvider>
    </CustomerBoundary>
  );
}

/**
 * Catches errors anywhere in its child component tree and renders a fallback component instead of the component that crashed.
 */
export function ErrorBoundary() {
  return (
    <>
      <RouteContentCommitSignal />
      <ErrorHandler />
    </>
  );
}

export function HydrateFallback() {
  return null;
}
